// textNode.js

import { useState, useRef, useEffect } from 'react';
import { Position } from 'reactflow';
import { BaseNode } from './BaseNode';

export const TextNode = ({ id, data }) => {
  const [currText, setCurrText] = useState(data?.text || '{{input}}');
  const [variables, setVariables] = useState([]);
  const textareaRef = useRef(null);

  // Parse variables from text on every change
  useEffect(() => {
    // Regex matches {{variableName}} where variableName follows JS variable rules
    // Starts with letter, _, or $ and followed by word characters
    const regex = /{{\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\s*}}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(currText)) !== null) {
      matches.push(match[1]);
    }

    // Keep only unique variables to avoid duplicate handles
    const uniqueVariables = [...new Set(matches)];
    setVariables(uniqueVariables);

    // Dynamic Resizing Logic
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

      // Auto-expand width based on longest line. (Simple approximation)
      const lines = currText.split('\n');
      const maxLength = Math.max(...lines.map(line => line.length));
      // Base width is 200px, but allow it to grow if text is very long
      const newWidth = Math.max(200, maxLength * 8 + 40);
      textareaRef.current.style.width = `${newWidth}px`;
    }
  }, [currText]);

  const handleTextChange = (e) => {
    setCurrText(e.target.value);
  };

  // Generate dynamic handles array
  // We evenly space the variables along the left side depending on how many there are.
  const dynamicHandles = variables.map((variable, index) => {
    // Calculate Y position percentage based on index and total variables
    // E.g., if 1 variable -> 50%, if 2 variables -> 33%, 66%
    const topPosition = `${((index + 1) / (variables.length + 1)) * 100}%`;

    return {
      type: 'target',
      position: Position.Left,
      id: `${id}-${variable}`,
      style: { top: topPosition }
    };
  });

  // Always include standard output handle
  const allHandles = [
    ...dynamicHandles,
    { type: 'source', position: Position.Right, id: `${id}-output` }
  ];

  return (
    <BaseNode
      id={id}
      title="Text"
      handles={allHandles}
      style={{
        width: textareaRef.current?.style.width || '200px', // Dynamically update parent container width
        transition: 'width 0.1s ease-in-out'
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', flexGrow: 1 }}>
        Text:
        <textarea
          ref={textareaRef}
          value={currText}
          onChange={handleTextChange}
          style={{
            marginTop: '4px',
            resize: 'none', // Disable manual resizing since it's auto
            overflow: 'hidden',
            minHeight: '40px',
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            fontSize: '14px',
            padding: '4px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </label>
    </BaseNode>
  );
}
